/**
 * The payment-method schedule shown in the coverage section: every market with the banks,
 * wallets and networks it takes, plus its minimums. Mirrors `xquotes-web`'s
 * `paymentMethods.ts` / `paymentLimits.ts` and the logos copied from it into
 * `public/images/payments/<folder>/<file>.png`. Deposits and payouts run on the same
 * methods everywhere; only the minimum differs.
 */
export type Direction = 'deposit' | 'payout';
export const DIRECTIONS: readonly Direction[] = ['deposit', 'payout'];

export type MethodKind = 'bank' | 'wallet' | 'crypto';
/** Row order inside a market panel: wallets lead, then banks, then the networks. */
export const METHOD_KINDS: readonly MethodKind[] = ['wallet', 'bank', 'crypto'];

export interface PaymentMethod {
  name: string;
  /** `<folder>/<file>` under `public/images/payments`; the file stem doubles as the fallback monogram. */
  logo: string;
  kind: MethodKind;
}

export interface Market {
  /** Tab / panel id and the ScrollTrigger-safe slug. */
  id: string;
  /** Short mono badge shown in place of a flag. */
  code: string;
  name: string;
  /** ISO code the minimums are quoted in. */
  currency: string;
  minimum: Record<Direction, string>;
  methods: readonly PaymentMethod[];
}

/** Shared "Online Bank Transfer" rail; the flag on each badge marks the market it belongs to. */
const ONLINE_BANK_TRANSFER = 'Online Bank Transfer';

const shopeePay: PaymentMethod = { name: 'ShopeePay', logo: 'shared/spp', kind: 'wallet' };
const bigPay: PaymentMethod = { name: 'BigPay', logo: 'shared/bgp', kind: 'wallet' };

export const markets: readonly Market[] = [
  {
    // Not tied to a country, so USDT leads ahead of the local-payment markets.
    id: 'usdt',
    code: '₮',
    name: 'USDT',
    currency: 'USDT',
    minimum: { deposit: '1', payout: '10' },
    methods: [
      { name: 'USDT — TRC20', logo: 'crypto/trc20', kind: 'crypto' },
      { name: 'USDT — BEP20', logo: 'crypto/bep20', kind: 'crypto' },
    ],
  },
  {
    id: 'malaysia',
    code: 'MY',
    name: 'Malaysia',
    currency: 'MYR',
    minimum: { deposit: '100', payout: '100' },
    methods: [
      { name: 'Maybank', logo: 'malaysia/may', kind: 'bank' },
      { name: 'CIMB', logo: 'malaysia/cimb', kind: 'bank' },
      { name: 'Public Bank', logo: 'malaysia/pbb', kind: 'bank' },
      { name: 'RHB Bank', logo: 'malaysia/rhb', kind: 'bank' },
      { name: 'MyPB', logo: 'malaysia/mypb', kind: 'bank' },
      { name: ONLINE_BANK_TRANSFER, logo: 'malaysia/obtmy', kind: 'bank' },
      { name: 'Touch ’n Go eWallet', logo: 'malaysia/tng', kind: 'wallet' },
      { name: 'GrabPay', logo: 'malaysia/grb', kind: 'wallet' },
      shopeePay,
      bigPay,
      { name: 'DuitNow QR', logo: 'malaysia/dnqr', kind: 'wallet' },
    ],
  },
  {
    id: 'thailand',
    code: 'TH',
    name: 'Thailand',
    currency: 'THB',
    minimum: { deposit: '100', payout: '100' },
    methods: [
      { name: 'Bangkok Bank', logo: 'thailand/bbl', kind: 'bank' },
      { name: 'Kasikornbank (KBank)', logo: 'thailand/kbk', kind: 'bank' },
      { name: 'Krungthai Bank', logo: 'thailand/ktb', kind: 'bank' },
      { name: 'Siam Commercial Bank (SCB)', logo: 'thailand/scb', kind: 'bank' },
      { name: 'K PLUS', logo: 'thailand/kplus', kind: 'bank' },
      { name: ONLINE_BANK_TRANSFER, logo: 'thailand/obtth', kind: 'bank' },
      { name: 'TrueMoney Wallet', logo: 'thailand/tmn', kind: 'wallet' },
      { name: 'PromptPay', logo: 'thailand/ppy', kind: 'wallet' },
      shopeePay,
      bigPay,
      { name: 'SCB Planet Plus', logo: 'thailand/scbp', kind: 'wallet' },
      { name: 'Thai QR Payment', logo: 'thailand/thqr', kind: 'wallet' },
    ],
  },
  {
    id: 'vietnam',
    code: 'VN',
    name: 'Vietnam',
    currency: 'VND',
    minimum: { deposit: '50,000', payout: '50,000' },
    methods: [
      { name: 'Vietcombank', logo: 'vietnam/vcb', kind: 'bank' },
      { name: 'BIDV', logo: 'vietnam/bidv', kind: 'bank' },
      { name: 'VietinBank', logo: 'vietnam/vtb', kind: 'bank' },
      { name: 'Techcombank', logo: 'vietnam/tcb', kind: 'bank' },
      { name: 'VCB Digibank', logo: 'vietnam/vcbd', kind: 'bank' },
      { name: ONLINE_BANK_TRANSFER, logo: 'vietnam/obtvn', kind: 'bank' },
      { name: 'MoMo', logo: 'vietnam/momo', kind: 'wallet' },
      { name: 'ZaloPay', logo: 'vietnam/zlp', kind: 'wallet' },
      shopeePay,
      { name: 'VietQR', logo: 'vietnam/vnqr', kind: 'wallet' },
    ],
  },
  {
    id: 'indonesia',
    code: 'ID',
    name: 'Indonesia',
    currency: 'IDR',
    minimum: { deposit: '100,000', payout: '100,000' },
    methods: [
      { name: 'Bank Central Asia (BCA)', logo: 'indonesia/bca', kind: 'bank' },
      { name: 'Bank Mandiri', logo: 'indonesia/mdr', kind: 'bank' },
      { name: 'Bank Rakyat Indonesia (BRI)', logo: 'indonesia/bri', kind: 'bank' },
      { name: 'Bank Negara Indonesia (BNI)', logo: 'indonesia/bni', kind: 'bank' },
      { name: ONLINE_BANK_TRANSFER, logo: 'indonesia/obtid', kind: 'bank' },
      { name: 'GoPay', logo: 'indonesia/gop', kind: 'wallet' },
      { name: 'DANA', logo: 'indonesia/dana', kind: 'wallet' },
      { name: 'OVO', logo: 'indonesia/ovo', kind: 'wallet' },
      { name: 'LinkAja', logo: 'indonesia/lnka', kind: 'wallet' },
      { name: 'QRIS', logo: 'indonesia/qris', kind: 'wallet' },
    ],
  },
  {
    id: 'india',
    code: 'IN',
    name: 'India',
    currency: 'INR',
    minimum: { deposit: '100', payout: '100' },
    methods: [
      { name: 'State Bank of India (SBI)', logo: 'india/sbi', kind: 'bank' },
      { name: 'HDFC Bank', logo: 'india/hdfc', kind: 'bank' },
      { name: 'ICICI Bank', logo: 'india/icici', kind: 'bank' },
      { name: ONLINE_BANK_TRANSFER, logo: 'india/obtin', kind: 'bank' },
      { name: 'PhonePe', logo: 'india/ppe', kind: 'wallet' },
      { name: 'Google Pay', logo: 'india/gpay', kind: 'wallet' },
      { name: 'Paytm', logo: 'india/ptm', kind: 'wallet' },
      { name: 'UPI', logo: 'india/upi', kind: 'wallet' },
    ],
  },
  {
    id: 'philippines',
    code: 'PH',
    name: 'Philippines',
    currency: 'PHP',
    minimum: { deposit: '100', payout: '100' },
    methods: [
      { name: 'BDO Unibank', logo: 'philippines/bdo', kind: 'bank' },
      { name: 'Bank of the Philippine Islands (BPI)', logo: 'philippines/bpi', kind: 'bank' },
      { name: 'Metrobank', logo: 'philippines/mbt', kind: 'bank' },
      { name: ONLINE_BANK_TRANSFER, logo: 'philippines/obtph', kind: 'bank' },
      { name: 'GCash', logo: 'philippines/gc', kind: 'wallet' },
      { name: 'Maya', logo: 'philippines/mya', kind: 'wallet' },
      { name: 'QRPH', logo: 'philippines/qrph', kind: 'wallet' },
    ],
  },
  {
    id: 'mexico',
    code: 'MX',
    name: 'Mexico',
    currency: 'MXN',
    minimum: { deposit: '40', payout: '40' },
    methods: [
      { name: 'BBVA México', logo: 'mexico/bbva', kind: 'bank' },
      { name: 'Banorte', logo: 'mexico/bnt', kind: 'bank' },
      { name: 'Santander México', logo: 'mexico/san', kind: 'bank' },
      { name: ONLINE_BANK_TRANSFER, logo: 'mexico/obtmx', kind: 'bank' },
      { name: 'Mercado Pago', logo: 'mexico/mp', kind: 'wallet' },
      { name: 'Spin by OXXO', logo: 'mexico/spn', kind: 'wallet' },
      { name: 'Nu México', logo: 'mexico/nu', kind: 'wallet' },
      { name: 'SPEI', logo: 'mexico/spei', kind: 'wallet' },
    ],
  },
  {
    id: 'pakistan',
    code: 'PK',
    name: 'Pakistan',
    currency: 'PKR',
    minimum: { deposit: '100', payout: '100' },
    methods: [
      { name: 'Habib Bank Limited (HBL)', logo: 'pakistan/hbl', kind: 'bank' },
      { name: 'United Bank Limited (UBL)', logo: 'pakistan/ubl', kind: 'bank' },
      { name: 'Meezan Bank', logo: 'pakistan/mzn', kind: 'bank' },
      { name: ONLINE_BANK_TRANSFER, logo: 'pakistan/obtpk', kind: 'bank' },
      { name: 'Easypaisa', logo: 'pakistan/eps', kind: 'wallet' },
      { name: 'JazzCash', logo: 'pakistan/jzc', kind: 'wallet' },
      { name: 'SadaPay', logo: 'pakistan/sdp', kind: 'wallet' },
    ],
  },
  {
    id: 'singapore',
    code: 'SG',
    name: 'Singapore',
    currency: 'SGD',
    minimum: { deposit: '5', payout: '5' },
    methods: [
      { name: 'OCBC', logo: 'singapore/ocbc', kind: 'bank' },
      shopeePay,
      bigPay,
      { name: 'Changi Pay', logo: 'singapore/chp', kind: 'wallet' },
      { name: 'DeCard', logo: 'singapore/dec', kind: 'wallet' },
      { name: 'dtcpay', logo: 'singapore/dtc', kind: 'wallet' },
      { name: 'Starryblu', logo: 'singapore/sblu', kind: 'wallet' },
    ],
  },
  {
    id: 'south-korea',
    code: 'KR',
    name: 'South Korea',
    currency: 'KRW',
    minimum: { deposit: '3,000', payout: '3,000' },
    methods: [
      { name: ONLINE_BANK_TRANSFER, logo: 'south-korea/obtkr', kind: 'bank' },
      { name: 'Kakao Pay', logo: 'south-korea/kkp', kind: 'wallet' },
      { name: 'Naver Pay', logo: 'south-korea/npay', kind: 'wallet' },
      { name: 'Toss Pay', logo: 'south-korea/toss', kind: 'wallet' },
    ],
  },
  {
    id: 'japan',
    code: 'JP',
    name: 'Japan',
    currency: 'JPY',
    minimum: { deposit: '300', payout: '300' },
    methods: [
      { name: ONLINE_BANK_TRANSFER, logo: 'japan/obtjp', kind: 'bank' },
      { name: 'PayPay', logo: 'japan/ppay', kind: 'wallet' },
    ],
  },
  {
    id: 'hong-kong',
    code: 'HK',
    name: 'Hong Kong SAR',
    currency: 'HKD',
    minimum: { deposit: '100', payout: '100' },
    methods: [
      { name: 'Hang Seng Bank', logo: 'hong-kong/hsb', kind: 'bank' },
      { name: 'AlipayHK', logo: 'hong-kong/ahk', kind: 'wallet' },
    ],
  },
  {
    id: 'macao',
    code: 'MO',
    name: 'Macao SAR',
    currency: 'MOP',
    minimum: { deposit: '100', payout: '100' },
    methods: [{ name: 'MPay', logo: 'macao/mpay', kind: 'wallet' }],
  },
  {
    id: 'china',
    code: 'CN',
    name: 'China',
    currency: 'CNY',
    minimum: { deposit: '100', payout: '100' },
    methods: [{ name: 'Alipay', logo: 'china/alp', kind: 'wallet' }],
  },
];

export interface MethodGroup {
  kind: MethodKind;
  methods: PaymentMethod[];
}

/** Splits a market's methods into their rendered columns, dropping kinds it has none of. */
export function groupMethods(market: Market): MethodGroup[] {
  return METHOD_KINDS.map((kind) => ({
    kind,
    methods: market.methods.filter((method) => method.kind === kind),
  })).filter((group) => group.methods.length > 0);
}

/**
 * Path of a logo under `public/`. Takes the bare `<market>/<file>` key so the coin stage,
 * which names rails without a `PaymentMethod`, can share the assets with the coverage cards.
 */
export function logoPath(logo: string): string {
  return `/images/payments/${logo}.png`;
}

/** Short uppercase stand-in shown on the plate when the logo image cannot load. */
export function logoMonogram(logo: string): string {
  return logo.slice(logo.lastIndexOf('/') + 1).toUpperCase();
}
